/* @moldura padrao */
/**
 * dev/telas-celular — a bancada para olhar as telas de celular no celular.
 *
 * Nasceu em 25/08/2026, a pedido dele, por um motivo simples: **não dá para
 * julgar tela de celular olhando o notebook.** A régua da decisão 5 ("o que
 * `pronto para celular` significa") não pode ser cravada de cima — precisa de
 * alguém abrindo no aparelho, com o dedo, e dizendo o que incomoda.
 *
 * Então esta tela não decide nada: ela só **junta num lugar** o que precisa ser
 * olhado, para ele abrir no app instalado e passar de uma para a outra sem
 * decorar endereço.
 *
 * ⚠️ É bancada, não funcionalidade. Vive em `/dev/` junto com
 * `campos-numericos`, aparece só para quem administra o app, e sai quando a
 * decisão 5 fechar. Nada aqui é usado pela operação.
 */
import { Link } from 'react-router-dom';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

/** Uma tela que precisa ser olhada no celular, e o que olhar nela. */
interface Alvo {
  rota: string;
  nome: string;
  quem: string;
  olhar: string;
  /** `true` = já é tela exclusiva de celular; `false` = tela de escritório que
   *  alguém pode abrir no celular assim mesmo. */
  exclusiva: boolean;
}

/**
 * As quatro telas de celular que o app tem hoje, mais as três de escritório que
 * alguém abre no celular de qualquer jeito. Medido em 25/08 — se aparecer uma
 * nova, ela entra aqui.
 *
 * ⚠️ Nenhuma delas usa ainda o sufixo `-celular` da convenção (§8 da lei). O
 * nome de arquivo muda junto com a rota, e rota é endereço que pode estar no
 * celular de alguém — por isso não foi feito de passagem.
 */
const ALVOS: Alvo[] = [
  {
    rota: '/inbox',
    nome: 'Inbox',
    quem: 'todo mundo — e a Adriana no sofá, decidindo autorização',
    olhar:
      'As três abas ROLAM ou espremem? (`overflow-x-auto` + `flex-nowrap`). ' +
      'O botão "Analisar" do cartão de decisão cai abaixo da dobra? ' +
      'E o cartão de decisão se distingue do de pendência sem precisar ler?',
    // Não é exclusiva: é uma tela só, que rearranja. A régua do projeto diz que
    // só ganha tela própria de celular o que é OUTRO TRABALHO no aparelho — e
    // aqui é o mesmo trabalho, com menos espaço.
    exclusiva: false,
  },
  {
    rota: '/despesas/nova',
    nome: 'Lançar despesa',
    quem: 'instalador, na rua, com a nota na mão',
    olhar:
      'É a mais crítica. Medido: 27 dos 43 alvos têm menos de 44px de altura — ' +
      '"Explicar melhor" e "Vincular a uma OS" têm 25px. Dá para acertar com o polegar?',
    exclusiva: true,
  },
  {
    rota: '/despesas/minhas',
    nome: 'Minhas despesas',
    quem: 'quem lançou, conferindo o que lançou',
    olhar: 'A lista rola demais? Dá para achar a despesa de ontem sem procurar?',
    exclusiva: true,
  },
  {
    rota: '/agenda/instalacoes',
    nome: 'Instalações da semana',
    quem: 'instalador, na caminhonete',
    olhar:
      'Tem dois modos (lista e calendário). O calendário serve no celular, ou ' +
      'só a lista? O cartão cabe sem apertar?',
    exclusiva: true,
  },
  {
    rota: '/lancar-despesa',
    nome: 'Despesa por link (sem login)',
    quem: 'quem recebe o link e não tem conta',
    olhar: 'A única que abre sem login. Funciona igual à de dentro do app?',
    exclusiva: true,
  },
  {
    rota: '/ordens-servico',
    nome: 'Lista de OS',
    quem: 'escritório — mas o instalador abre',
    olhar: 'Tela de escritório. Cabe, ou vira coluna espremida?',
    exclusiva: false,
  },
  {
    rota: '/autorizacoes',
    nome: 'Autorizações',
    quem: 'a mãe dele, autorizando pelo celular',
    olhar:
      'Ele já disse que este é caso de consulta no celular: entrar, ver e ' +
      'aprovar. Dá para aprovar sem abrir o notebook?',
    exclusiva: false,
  },
  {
    rota: '/viagens',
    nome: 'Viagens',
    quem: 'quem quer ver quanto custou uma viagem',
    olhar: 'Consulta pontual pelo celular — cabe?',
    exclusiva: false,
  },
];

export function DevTelasCelularPage() {
  const exclusivas = ALVOS.filter((a) => a.exclusiva);
  const escritorio = ALVOS.filter((a) => !a.exclusiva);

  return (
    <CustomMolduraPagina
      titulo="Telas no celular"
      descricao="Bancada de teste — abra no celular e diga o que incomoda."
      semCaminho
    >

      <div className="card adminuiux-card mb-3">
        <div className="card-body d-flex gap-3">
          <i className="bi bi-phone fs-4 text-theme-1" />
          <div className="small">
            <p className="mb-2">
              Esta página existe para responder <strong>uma pergunta só</strong>:
              o que “pronto para celular” significa neste app. Hoje não há régua
              nenhuma, então “está bom no celular” é opinião de quem olhou.
            </p>
            <p className="mb-0 text-secondary">
              Abra cada uma no aparelho, use de verdade, e anote o que atrapalha.
              O que você disser vira a regra — e depois um script consegue cobrar.
            </p>
          </div>
        </div>
      </div>

      <h2 className="h6 text-uppercase text-secondary small mb-2">
        Telas feitas para o celular
      </h2>
      <div className="row g-3 mb-4">
        {exclusivas.map((a) => (
          <CartaoAlvo key={a.rota} alvo={a} />
        ))}
      </div>

      <h2 className="h6 text-uppercase text-secondary small mb-2">
        Telas de escritório que alguém abre no celular
      </h2>
      <div className="row g-3">
        {escritorio.map((a) => (
          <CartaoAlvo key={a.rota} alvo={a} />
        ))}
      </div>

      <p className="text-secondary small mt-4 mb-0">
        Bancada de desenvolvimento — sai quando a régua estiver decidida. Ver a{' '}
        <span className="fw-medium">convenção visual §8</span>.
      </p>
    </CustomMolduraPagina>
  );
}

function CartaoAlvo({ alvo }: { alvo: Alvo }) {
  return (
    <div className="col-12 col-md-6">
      <div className="card adminuiux-card h-100">
        <div className="card-body d-flex flex-column">
          <div className="d-flex align-items-start gap-3 mb-2">
            <span
              className={`avatar avatar-40 rounded ${
                alvo.exclusiva
                  ? 'bg-theme-1 text-white'
                  : 'bg-theme-1-subtle text-theme-1'
              } d-flex align-items-center justify-content-center flex-shrink-0`}
            >
              <i className={alvo.exclusiva ? 'bi bi-phone' : 'bi bi-display'} />
            </span>
            <div className="me-auto">
              <h3 className="h6 mb-0">{alvo.nome}</h3>
              <p className="small text-secondary mb-0">{alvo.quem}</p>
            </div>
          </div>

          <p className="small mb-3">{alvo.olhar}</p>

          <Link
            to={alvo.rota}
            className="btn btn-theme mt-auto"
            /* Largura cheia aqui é de propósito: esta bancada é para ser usada
               NO celular, e é o caso em que o tema manda botão largo. */
          >
            Abrir <span className="text-white-50 ms-1">{alvo.rota}</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
